import React from 'react';
import SEO from '../components/SEO';

const H = ({ children }) => <h2 className="text-lg font-bold text-[#C0A062] mt-8 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-zinc-300 leading-relaxed mb-3">{children}</p>;
const LI = ({ children }) => <li className="text-zinc-300 leading-relaxed">{children}</li>;

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4 sm:px-6 lg:px-8" data-testid="privacy-page">
      <SEO title="Privacy Policy | DojoDash" description="How DojoDash collects, uses, and protects your data." />
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Privacy Policy — DojoDash</h1>
        <p className="text-zinc-500 text-sm">Effective: August 26, 2026</p>
        <p className="text-zinc-500 text-sm mb-8">Last updated: August 26, 2026</p>

        <P>DojoDash ("we," "us," or "our") operates the DojoDash platform, connecting clients with independent coaches for in-person training sessions. This Privacy Policy explains what data we collect, how we use it, and your rights.</P>

        <H>1. What we collect</H>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <LI>Account information: name, email address, phone number</LI>
          <LI>Profile information: city, zip code, training preferences, bio (coaches)</LI>
          <LI>Location data: your device&apos;s GPS coordinates, collected only when you trigger a Safety SOS alert</LI>
          <LI>Session data: booking details including session address, date, time, category</LI>
          <LI>Reviews and ratings you submit</LI>
          <LI>Payment information: processed entirely by Stripe — we do not store card numbers or bank account details</LI>
          <LI>Device and usage data: IP address, browser type, pages visited (for security and debugging)</LI>
        </ul>

        <H>2. How we use your data</H>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <LI>To operate the platform: match clients with coaches, process bookings, send session confirmations</LI>
          <LI>To process payments: your payment details are handled by Stripe, Inc. (see stripe.com/privacy)</LI>
          <LI>For safety features: location data is stored only when you activate an SOS alert</LI>
          <LI>To communicate with you: booking confirmations, session reminders, support responses</LI>
          <LI>To improve DojoDash: aggregate usage analytics, bug fixes</LI>
        </ul>

        <H>3. Who we share your data with</H>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <LI>Stripe, Inc. — payment processing</LI>
          <LI>SendGrid (Twilio) — transactional email delivery</LI>
          <LI>Checkr, Inc. — background check processing for coach verification (coaches only)</LI>
          <LI>We do not sell your personal data to advertisers or third parties</LI>
        </ul>

        <H>4. Coach profile data</H>
        <P>Coaches&apos; names, profile photos, training categories, credentials, and public ratings are visible to all users and visitors. Coaches&apos; phone numbers and addresses are not displayed publicly.</P>

        <H>5. Data retention</H>
        <P>We retain account data for as long as your account is active. If an account is inactive for 24 months, we will delete or anonymize associated personal data, except where retention is required for legal, safety, or dispute-resolution purposes. You may request account deletion by contacting us at support@dojodash.net — deletion requests are processed within 30 days, except where retention is required by law.</P>

        <H>6. Location data</H>
        <P>Location is collected only when you activate the SOS safety feature. It is stored in our database tied to the alert record. It is not collected passively or in the background.</P>

        <H>7. Children</H>
        <P>DojoDash is strictly for users 18 and older. We do not knowingly collect data from anyone under 18. If we learn an account belongs to a minor, we will terminate the account and delete associated data.</P>

        <H>8. Your rights</H>
        <P>You may request access to, correction of, or deletion of your personal data at any time by emailing support@dojodash.net.</P>

        <H>9. Your Privacy Rights</H>
        <P>Depending on your state of residence, you may have the right to know what personal data we collect, request deletion of your data, and opt out of the sale or sharing of your data. DojoDash does not sell personal data. To exercise these rights, contact support@dojodash.net.</P>

        <H>10. Changes to this policy</H>
        <P>We may update this policy as the platform evolves. We will notify users of material changes via email or in-app notice.</P>

        <H>11. Contact</H>
        <P>DojoDash / Sector-V LLC<br />support@dojodash.net</P>
      </div>
    </div>
  );
}
